// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import {
  getFirestore,
  addDoc,
  doc,
  collection,
  serverTimestamp,
  increment,
  setDoc,
} from "firebase/firestore";
import { useState, useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKInput from "components/MKInput";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";
import Logo from "assets/images/logos/Logo.svg";
import Swal from "sweetalert2";

function Contact({ isModal = false, onClose = null }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [secondaryContact, setSecondaryContact] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("");
  const [detectedCountry, setDetectedCountry] = useState("eg");
  const [errors, setErrors] = useState({
    name: false,
    email: false,
    phone: false,
    subject: false,
  });
  const [subject, setSubject] = useState("");
  const [meetingTime, setMeetingTime] = useState("");
  const [meetingDate, setMeetingDate] = useState("");

  const db = getFirestore();

  const resetForm = () => {
    setName("");
    setEmail("");
    setSecondaryContact("");
    setSubject("");
    setMeetingTime("");
    setMeetingDate("");
    setErrors({ name: false, email: false, phone: false, subject: false });
  };

  useEffect(() => {
    try {
      // Try timezone-based country detection FIRST (more accurate for physical location)
      const getCountryFromTimezone = () => {
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

          // Map common timezones to countries
          const tzMap = {
            "Africa/Cairo": "eg",
            "Africa/Lagos": "ng",
            "Africa/Johannesburg": "za",
            "Africa/Nairobi": "ke",
            "Africa/Algiers": "dz",
            "Africa/Casablanca": "ma",
            "America/New_York": "us",
            "America/Los_Angeles": "us",
            "America/Chicago": "us",
            "America/Denver": "us",
            "America/Toronto": "ca",
            "America/Vancouver": "ca",
            "America/Mexico_City": "mx",
            "America/Sao_Paulo": "br",
            "America/Buenos_Aires": "ar",
            "Europe/London": "gb",
            "Europe/Paris": "fr",
            "Europe/Berlin": "de",
            "Europe/Madrid": "es",
            "Europe/Rome": "it",
            "Europe/Amsterdam": "nl",
            "Europe/Brussels": "be",
            "Europe/Vienna": "at",
            "Europe/Stockholm": "se",
            "Europe/Warsaw": "pl",
            "Europe/Athens": "gr",
            "Europe/Istanbul": "tr",
            "Europe/Moscow": "ru",
            "Asia/Dubai": "ae",
            "Asia/Riyadh": "sa",
            "Asia/Kuwait": "kw",
            "Asia/Beirut": "lb",
            "Asia/Jerusalem": "il",
            "Asia/Tokyo": "jp",
            "Asia/Shanghai": "cn",
            "Asia/Hong_Kong": "hk",
            "Asia/Singapore": "sg",
            "Asia/Bangkok": "th",
            "Asia/Manila": "ph",
            "Asia/Seoul": "kr",
            "Asia/Karachi": "pk",
            "Asia/Kolkata": "in",
            "Australia/Sydney": "au",
            "Australia/Melbourne": "au",
            "Pacific/Auckland": "nz",
          };

          if (tzMap[tz]) {
            return tzMap[tz];
          }

          // Fallback: try to extract from timezone parts
          const parts = tz.split("/");
          if (parts.length >= 2) {
            const continent = parts[0];
            const city = parts[1];

            // Africa continent fallback
            if (continent === "Africa") {
              const africaCities = {
                Cairo: "eg",
                Lagos: "ng",
                Johannesburg: "za",
                Nairobi: "ke",
                Algiers: "dz",
                Casablanca: "ma",
                Tunis: "tn",
                Tripoli: "ly",
              };
              if (africaCities[city]) {
                return africaCities[city];
              }
            }
          }
        } catch (e) {
          console.error("Timezone detection error:", e);
        }
        return null;
      };

      // Try locale-based detection as secondary fallback
      const getCountryFromLocale = () => {
        try {
          const nav = typeof navigator !== "undefined" ? navigator : null;
          let locale = "";
          if (nav)
            locale = (nav.languages && nav.languages[0]) || nav.language || "";

          if (!locale) return null;

          // Try Intl.Locale API
          if (typeof Intl !== "undefined" && Intl.Locale) {
            try {
              const L = new Intl.Locale(locale);
              if (L.region) return L.region.toLowerCase();
              if (L.maximize) {
                const maximized = L.maximize();
                if (maximized && maximized.region)
                  return maximized.region.toLowerCase();
              }
            } catch (e) {
              // Continue to manual parsing
            }
          }

          // Manual parsing - only use region part (after dash/underscore)
          const parts = locale.replace("_", "-").split("-");
          if (
            parts.length > 1 &&
            parts[1].length === 2 &&
            /^[A-Za-z]{2}$/.test(parts[1])
          ) {
            return parts[1].toLowerCase();
          }
        } catch (e) {
          // Locale detection failed
        }
        return null;
      };

      // Priority: timezone > locale > default 'eg'
      const tzCountry = getCountryFromTimezone();

      const localeCountry = tzCountry ? null : getCountryFromLocale();

      const finalCountry = tzCountry || localeCountry || "eg";

      setDetectedCountry(finalCountry);
    } catch (e) {
      console.error("Country detection failed completely:", e);
      setDetectedCountry("eg");
    }
  }, []);

  const handleSubmit = async () => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const newErrors = {
      name: !name.trim(),
      phone: !secondaryContact || !secondaryContact.toString().trim(),
      subject: !subject.trim(),
      email: !email.trim() || !emailRegex.test(email),
    };
    setErrors(newErrors);
    if (
      newErrors.name ||
      newErrors.phone ||
      newErrors.subject ||
      newErrors.email
    ) {
      Swal.fire({
        title: "Error",
        text: "Please fill all required fields and correct invalid values",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    // Validate phone number according to selected country
    try {
      const raw = secondaryContact || "";
      const withPlus = raw.startsWith("+") ? raw : `+${raw}`;
      const parsed = parsePhoneNumberFromString(withPlus);
      if (!parsed || !parsed.isValid()) {
        setErrors((p) => ({ ...p, phone: true }));
        Swal.fire({
          title: "Error",
          text: "Please enter a valid phone number",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }
      if (
        phoneCountry &&
        parsed.country &&
        parsed.country.toLowerCase() !== phoneCountry.toLowerCase()
      ) {
        setErrors((p) => ({ ...p, phone: true }));
        Swal.fire({
          title: "Error",
          text: `The phone number doesn't match the selected country (${phoneCountry.toUpperCase()})`,
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }
    } catch (e) {
      setErrors((p) => ({ ...p, phone: true }));
      Swal.fire({
        title: "Error",
        text: "Invalid phone number format",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const collectionRef = collection(db, "consultationProgram");
      const res = await addDoc(collectionRef, {
        name,
        email,
        secondaryContact,
        subject,
        meetingTime,
        meetingDate,
        createdAt: serverTimestamp(),
        timezone,
      });

      const docRef = doc(db, "consultationProgram", "consultationProgramData");
      await setDoc(docRef, { count: increment(1) }, { merge: true });

      resetForm();

      Swal.fire({
        title: "Success",
        text: `Your request has been submitted. We will contact you soon.`,
        icon: "success",
        confirmButtonText: "OK",
      });
      if (isModal && onClose) onClose();
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: "Something went wrong. Please try again later.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <MKBox
      component="section"
      data-ring="light"
      py={{ xs: 0, lg: isModal ? 0 : 6 }}
      sx={{
        height: isModal ? "100%" : "auto",
        display: isModal ? "flex" : "block",
        flexDirection: "column",
      }}
    >
      <Container
        sx={{
          height: isModal ? "100%" : "auto",
          display: isModal ? "flex" : "block",
          flexDirection: "column",
          mt: isModal ? { xs: 0, sm: 4 } : 0,
        }}
      >
        <MKBox
          width="100%"
          borderRadius="xl"
          shadow="xl"
          mb={isModal ? 0 : 6}
          sx={{
            overflow: isModal ? "auto" : "hidden",
            height: isModal ? { xs: "92%", sm: "100%" } : "auto",
            display: "flex",
            flexDirection: "column",
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
            "&::-webkit-scrollbar": { display: "none", width: 0 },
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            position: "relative",
          }}
        >
          {isModal && onClose && (
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                zIndex: 1400,
                backgroundColor: "white",
                borderRadius: "50%",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                color: "rgba(0,0,0,0.6)",
                p: 0.6,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  color: "rgba(0,0,0,0.87)",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}

          {isModal ? (
            <Grid container sx={{ height: "100%", minHeight: 0 }}>
              <Grid
                item
                xs={12}
                md={5}
                sx={{
                  background:
                    "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                  color: "white",
                  p: { xs: 2, md: 3 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <Box
                  component="img"
                  src={Logo}
                  alt="logo"
                  sx={{ height: { xs: 50, sm: 90 }, mb: 1.5 }}
                />
                <MKTypography
                  variant="h5"
                  fontWeight={700}
                  mb={1}
                  color="white"
                  sx={{
                    letterSpacing: "-0.02em",
                    lineHeight: "1.1",
                    color: "white",
                    fontSize: { xs: "1.05rem", sm: "1.25rem" },
                  }}
                >
                  The DevHouse
                </MKTypography>
                <MKTypography
                  variant="body2"
                  fontWeight={400}
                  color="white"
                  sx={{
                    lineHeight: { xs: "1.2", sm: "1.4" },
                    opacity: 0.95,
                    color: "white",
                    fontSize: { xs: "0.78rem", sm: "0.9rem" },
                  }}
                >
                  Operational systems for businesses that outgrow generic tools.
                </MKTypography>
              </Grid>

              <Grid
                item
                xs={12}
                md={7}
                sx={{
                  height: "100%",
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  background: "white",
                  borderRadius: { xs: "0 0 12px 12px", md: "0 12px 12px 0" },
                  boxShadow: "none",
                  "&::-webkit-scrollbar": { display: "none", width: 0 },
                }}
              >
                <MKBox
                  sx={{
                    p: { xs: 3, sm: 5 },
                    flex: 1,
                    overflowY: "auto",
                    maxHeight: "100%",
                    WebkitOverflowScrolling: "touch",
                    "&::-webkit-scrollbar": { display: "none", width: 0 },
                  }}
                >
                  <MKTypography
                    variant="h6"
                    color="black"
                    mb={4}
                    textAlign="left"
                    fontWeight="600"
                    sx={{
                      letterSpacing: "-0.01em",
                    }}
                  >
                    Schedule Your Consultation
                  </MKTypography>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <MKInput
                        variant="standard"
                        label="Full Name *"
                        placeholder="Enter your name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        error={errors.name}
                        helperText={errors.name ? "Required" : ""}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <MKInput
                        variant="standard"
                        label="Email Address *"
                        placeholder="example@domain.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        error={errors.email}
                        helperText={
                          errors.email
                            ? !email.trim()
                              ? "Required"
                              : "Enter a valid email"
                            : ""
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <PhoneInput
                        country={detectedCountry || "eg"}
                        value={secondaryContact}
                        onChange={(phone, country) => {
                          setSecondaryContact(phone);
                          setPhoneCountry(
                            country?.countryCode || country?.iso2 || "",
                          );
                        }}
                        containerStyle={{
                          width: "100%",
                          borderBottom: "1px solid rgba(0,0,0,0.42)",
                          position: "relative",
                        }}
                        inputStyle={{
                          border: "none",
                          boxShadow: "none",
                          paddingLeft: "52px",
                          paddingTop: "6px",
                          paddingBottom: "6px",
                          paddingRight: "12px",
                          width: "100%",
                          fontSize: "1rem",
                          background: "transparent",
                        }}
                        buttonStyle={{
                          position: "absolute",
                          left: 0,
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "transparent",
                          border: "none",
                        }}
                        dropdownStyle={{ zIndex: 1400 }}
                        inputProps={{ name: "phone", required: true }}
                        specialLabel="Phone Number *"
                      />
                      {errors.phone && (
                        <div
                          style={{
                            color: "#d32f2f",
                            fontSize: "0.75rem",
                            marginTop: 6,
                          }}
                        >
                          Please enter a valid phone number
                        </div>
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <MKInput
                        variant="standard"
                        label="Subject *"
                        placeholder="What's this consultation about?"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        error={errors.subject}
                        helperText={errors.subject ? "Required" : ""}
                        multiline
                        rows={2}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <MKInput
                        type="date"
                        variant="standard"
                        label="Preferred Date"
                        value={meetingDate}
                        onChange={(e) => setMeetingDate(e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <MKInput
                        type="time"
                        variant="standard"
                        label="Preferred Time"
                        value={meetingTime}
                        onChange={(e) => setMeetingTime(e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <MKButton
                        variant="gradient"
                        sx={{
                          color: "rgba(255, 255, 255, 1)",
                          background:
                            "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                          width: "100%",
                          padding: "10px 28px",
                          fontSize: "0.95rem",
                          fontWeight: 600,
                          textTransform: "none",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)",
                            transform: "translateY(-2px)",
                            boxShadow: "0 8px 16px rgba(59, 130, 246, 0.3)",
                          },
                        }}
                        onClick={handleSubmit}
                      >
                        Send Request
                      </MKButton>
                    </Grid>
                  </Grid>
                </MKBox>
              </Grid>
            </Grid>
          ) : (
            <MKBox p={{ xs: 3, sm: 5 }}>
              <MKTypography
                variant="h6"
                color="black"
                mb={4}
                textAlign="left"
                fontWeight="600"
                sx={{
                  letterSpacing: "-0.01em",
                }}
              >
                Schedule Your Consultation
              </MKTypography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MKInput
                    variant="standard"
                    label="Full Name *"
                    placeholder="Enter your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={errors.name}
                    helperText={errors.name ? "Required" : ""}
                  />
                </Grid>

                <Grid item xs={12}>
                  <MKInput
                    variant="standard"
                    label="Email Address *"
                    placeholder="example@domain.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={errors.email}
                    helperText={
                      errors.email
                        ? !email.trim()
                          ? "Required"
                          : "Enter a valid email"
                        : ""
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <PhoneInput
                    country={detectedCountry || "eg"}
                    value={secondaryContact}
                    onChange={(phone, country) => {
                      setSecondaryContact(phone);
                      setPhoneCountry(
                        country?.countryCode || country?.iso2 || "",
                      );
                    }}
                    containerStyle={{
                      width: "100%",
                      borderBottom: "1px solid rgba(0,0,0,0.42)",
                      position: "relative",
                    }}
                    inputStyle={{
                      border: "none",
                      boxShadow: "none",
                      paddingLeft: "72px",
                      paddingTop: "6px",
                      paddingBottom: "6px",
                      paddingRight: "12px",
                      width: "100%",
                      fontSize: "1rem",
                      background: "transparent",
                    }}
                    buttonStyle={{
                      position: "absolute",
                      left: 0,
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "transparent",
                      border: "none",
                    }}
                    dropdownStyle={{ zIndex: 1400 }}
                    inputProps={{ name: "phone", required: true }}
                    specialLabel="Phone Number *"
                  />
                  {errors.phone && (
                    <div
                      style={{
                        color: "#d32f2f",
                        fontSize: "0.75rem",
                        marginTop: 6,
                      }}
                    >
                      Please enter a valid phone number
                    </div>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <MKInput
                    variant="standard"
                    label="Subject *"
                    placeholder="What's this consultation about?"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={errors.subject}
                    helperText={errors.subject ? "Required" : ""}
                    multiline
                    rows={4}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MKInput
                    type="date"
                    variant="standard"
                    label="Preferred Date"
                    value={meetingDate}
                    onChange={(e) => setMeetingDate(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MKInput
                    type="time"
                    variant="standard"
                    label="Preferred Time"
                    value={meetingTime}
                    onChange={(e) => setMeetingTime(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <MKButton
                    variant="gradient"
                    sx={{
                      color: "rgba(255, 255, 255, 1)",
                      background:
                        "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                      width: "100%",
                      padding: "10px 28px",
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      textTransform: "none",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 16px rgba(59, 130, 246, 0.3)",
                      },
                    }}
                    onClick={handleSubmit}
                  >
                    Send Request
                  </MKButton>
                </Grid>
              </Grid>
            </MKBox>
          )}
        </MKBox>
      </Container>
    </MKBox>
  );
}

export default Contact;
